import { onMounted, onUnmounted, Ref, isRef, ref } from "vue";

export type IntersectionObserverOptions = IntersectionObserverInit;

const indexAttributeName = "data-visibility-index";

/**
 * Watch one element in one IntersectionObserver.
 * There are implemented wrapper functions from vue and other functions for simplification.
 * @param targets watching targets. Targets can be from vue or from vanillajs.
 * @returns instance of observer and Ref<boolean> indicates the element is observed.
 */
export function useIntersectionObserver<T extends Element | Ref<Element>>(
    targets: T,
    callback?: IntersectionObserverCallback,
    options?: IntersectionObserverOptions
): {
    observer: IntersectionObserver;
    isIntersecting: Ref<boolean>;
};

/**
 * Watch many elements in one IntersectionObserver.
 * There are implemented wrapper functions from vue and other functions for simplification.
 * @param targets watching targets. Targets can be from vue or from vanillajs.
 * @returns instance of observer and array of Ref<boolean> where each index is tied to the index of the target in the array, which is observed.
 */
export function useIntersectionObserver<T extends Element[] | Ref<Element>[] | Ref<Element[]>>(
    targets: T,
    callback?: IntersectionObserverCallback,
    options?: IntersectionObserverOptions
): {
    observer: IntersectionObserver;
    isIntersecting: Ref<boolean[]>;
};

export function useIntersectionObserver<T extends Element | Ref<Element> | Element[] | Ref<Element>[] | Ref<Element[]>>(
    targets: T,
    callback?: IntersectionObserverCallback,
    options: IntersectionObserverOptions = { threshold: 0.66 }
) {
    let isSimpleVersion = false;
    const isIntersecting = ref<boolean[] | boolean>([]);

    /***************************** INIT VALUES ****************************/
    const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
            if (isSimpleVersion) {
                isIntersecting.value = entry.isIntersecting;
            } else {
                const index = +entry.target.getAttribute(indexAttributeName);
                isIntersecting.value[index] = entry.isIntersecting;
            }
        });

        if (callback) callback(entries, observer);
    }, options);

    const initObserver = (targets: Element[]) => {
        targets.forEach((target, index) => {
            if (isSimpleVersion) {
                isIntersecting.value = false;
            } else {
                target.setAttribute(indexAttributeName, index.toString());
                (isIntersecting.value as Array<boolean>).push(false);
            }

            observer.observe(target);
        });
    };

    /***************************** PARSE TYPE ****************************/
    if (isRef(targets)) {
        onMounted(() => {
            if (targets.value instanceof Array) {
                // Ref<Element[]>
                initObserver(targets.value);
            } else {
                // Ref<Element>
                isSimpleVersion = true;
                initObserver([targets.value]);
            }
        });

        onUnmounted(() => {
            observer.disconnect();
        });
    } else if (!(targets instanceof Array)) {
        // Element
        isSimpleVersion = true;
        initObserver([targets]);
    } else if (isRef(targets[0])) {
        // Ref<Element>[]
        onMounted(() => {
            initObserver(targets.map((targetRef) => targetRef.value));
        });

        onUnmounted(() => {
            observer.disconnect();
        });
    } else {
        // Element[]
        initObserver(targets as Element[]);
    }

    return {
        observer,
        isIntersecting,
    };
}
